import { useState, useCallback, useEffect, useMemo } from "react";
import {
  AccountDeletionAnalysis,
  AccountTableInfo,
  fetchAccountDeletionAnalysis,
} from "./AccountDeletionServices";

export type TableWithModel = AccountTableInfo & {
  model_id: string;
  model_name: string;
};

/// D42: deleting an account no longer hands anything to anybody. Every table
/// listed here dies with the account, and the only rescue is the copy window —
/// anyone still holding an import grant has 10 days after the purge to copy the
/// table into their own model. So this hook stages nothing: it lists what will
/// go, and the dialog offers the one pre-emptive action that exists (moving a
/// table into a model in a space you manage, which takes it out of this list).
export const useDeleteAccount = (isOpen: boolean) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AccountDeletionAnalysis | null>(
    null,
  );

  const initializeData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAnalysis(await fetchAccountDeletionAnalysis());
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to load account analysis",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      initializeData();
    } else {
      setAnalysis(null);
      setError(null);
    }
  }, [isOpen, initializeData]);

  const [allTables, criticalTables] = useMemo(() => {
    if (!analysis) {
      return [[], []] as [TableWithModel[], TableWithModel[]];
    }
    const all = analysis.models.flatMap((m) =>
      m.tables.map((t) => ({
        ...t,
        model_id: m.model_id,
        model_name: m.model_name,
      })),
    );
    return [all, all.filter((t) => t.usage_status === "Critical")];
  }, [analysis]);

  return {
    loading,
    error,
    analysis,
    allTables,
    criticalTables,
    /// Re-reads the analysis after a table is moved out, so the moved row
    /// disappears from the list it is no longer part of.
    refresh: initializeData,
  };
};
